import { app, auth, db } from "@/lib/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type DocumentData,
  type DocumentReference,
  type Transaction,
} from "firebase/firestore";
import type { User } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";

import {
  createDefaultBusinessHours,
  sanitizeBusinessHours,
} from "@/lib/businessHours";
import {
  generateBusinessAccountNumber,
  isValidBusinessAccountNumber,
} from "@/lib/accountNumbers";
import { WEB_BASE_URL, normalizeWebBaseUrl } from "@/lib/config/brand";
import { getActiveEntitlement } from "@/lib/entitlements/getActiveEntitlement";
import {
  normalizePioneerSlotStatus,
  PIONEER_SLOTS_COLLECTION,
} from "@/lib/pioneerSlots";
import {
  selectOwnerLifecycleBusiness,
  type OwnerOnboardingStep,
} from "@/lib/onboardingResume";
import {
  normalizeWhatsAppChatNumber,
  normalizeWhatsAppCtaType,
} from "@/lib/whatsapp";
import type { ItemPriceType, ItemPriceUnit } from "@/lib/itemPricing";
import { hasCurrentOwnerLegalAcceptance } from "@/lib/legalAcceptance";
import { LEGAL_VERSION } from "@/src/content/legalPolicies";

/* =====================================================
   TYPES
===================================================== */

export type BusinessItem = {
  id: string;
  name: string;
  description?: string;
  imageUri?: string;
  imageUrl?: string;
  imageFit?: "cover" | "contain";
  imageWidth?: number | null;
  imageHeight?: number | null;
  priceType?: ItemPriceType;
  priceAmount?: number | null;
  priceMin?: number | null;
  priceMax?: number | null;
  priceUnit?: ItemPriceUnit | null;
  customPriceUnit?: string | null;
  order: number;
  createdAt: number;
};

export type BusinessStatus =
  | "draft"
  | "pending"
  | "approved"
  | "rejected"
  | "changes_requested";

export const SAVED_BUSINESS_INCOMPLETE_MESSAGE =
  "Some saved business details are incomplete. Please fix the items below before submitting.";

const SAVED_BUSINESS_INCOMPLETE_CODE =
  "onboarding/saved-business-incomplete";

export class SavedBusinessValidationError extends Error {
  readonly code = SAVED_BUSINESS_INCOMPLETE_CODE;
  readonly missingRequirements: string[];

  constructor(missingRequirements: string[]) {
    const requirements = missingRequirements.length
      ? [...missingRequirements]
      : ["Complete all required onboarding details."];

    super(
      `${SAVED_BUSINESS_INCOMPLETE_MESSAGE}\n\n${requirements
        .map((item) => `- ${item}`)
        .join("\n")}`
    );

    this.name = "SavedBusinessValidationError";
    this.missingRequirements = requirements;
  }
}

export function getSavedBusinessValidationRequirements(
  error: unknown
): string[] {
  if (!error || typeof error !== "object") return [];

  const candidate = error as {
    code?: unknown;
    missingRequirements?: unknown;
    details?: unknown;
  };

  const details =
    candidate.details && typeof candidate.details === "object"
      ? (candidate.details as {
          code?: unknown;
          missingRequirements?: unknown;
        })
      : null;

  const validationCode = details?.code ?? candidate.code;
  const missingRequirements =
    details?.missingRequirements ?? candidate.missingRequirements;

  if (
    validationCode !== SAVED_BUSINESS_INCOMPLETE_CODE ||
    !Array.isArray(missingRequirements)
  ) {
    return [];
  }

  return missingRequirements
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.trim())
    .filter(Boolean);
}

export type BusinessType =
  | "physical_shop"
  | "freelancer"
  | "online_only"
  | null;

export type BusinessLocationPin =
  | {
      lat: number;
      lng: number;
      accuracyMeters?: number | null;
      source: "current_location";
      confirmedByOwner: boolean;

      placeName?: string | null;
      roadName?: string | null;
      landmarkName?: string | null;
      plusCode?: string | null;
      formattedAddress?: string | null;
    }
  | null;

export type CreateDraftBusinessInput = {
  ownerName?: string;
  businessName?: string;
  mobileNumber?: string;
};

export type CreateBusinessDraftPermissionSource =
  | "entitlement"
  | "pioneer";

export type CreateBusinessDraftPermission = {
  allowed: boolean;
  source?: CreateBusinessDraftPermissionSource;
  reason?: string;
  entitlementId?: string;
  pioneerSlotId?: string;
  maxBusinesses?: number;
  usedBusinesses?: number;
};

export type OwnerListingSubscriptionSummary = {
  source: "entitlement" | "pioneer" | "none";
  planName: string;
  status: string;
  active: boolean;

  id?: string;
  businessId?: string | null;

  maxBusinesses?: number;
  usedBusinesses?: number;

  createdAt?: unknown;
  updatedAt?: unknown;
  startedAt?: unknown;
  expiresAt?: unknown;
  validUntil?: unknown;
  endsAt?: unknown;
};

export type OwnerLegalAcceptance = {
  version: string;
  termsVersion: string;
  privacyVersion: string;
  refundVersion: string;
  listingGuidelinesVersion: string;
  acceptedAt: unknown;
  updatedAt: unknown;
};

type EntitlementData = {
  status?: string;
  isActive?: boolean;
  active?: boolean;

  planName?: string;
  plan?: string;
  type?: string;
  subscriptionType?: string;

  maxBusinesses?: number;
  businessLimit?: number;
  maxListings?: number;
  listingLimit?: number;

  usedBusinesses?: number;
  usedBusinessCount?: number;
  businessesUsed?: number;
  usedListings?: number;

  createdAt?: unknown;
  updatedAt?: unknown;
  startedAt?: unknown;
  expiresAt?: unknown;
  validUntil?: unknown;
  endsAt?: unknown;
};

type ExistingOwnerBusiness = {
  id: string;
  accountNumber?: string;
  status?: BusinessStatus;
  isDeleted?: boolean;
  updatedAt?: unknown;
  createdAt?: unknown;
};

type RawLocationPatch = {
  countyId?: unknown;
  countyName?: unknown;

  cityId?: unknown;
  cityName?: unknown;
  citySuggestionId?: unknown;
  citySuggestionName?: unknown;

  areaId?: unknown;
  areaName?: unknown;
  areaSuggestionId?: unknown;
  areaSuggestionName?: unknown;
};

/* =====================================================
   CONSTANTS
===================================================== */

const BUSINESS_COLLECTION = "businesses";
const BUSINESS_ACCOUNT_NUMBERS_COLLECTION = "businessAccountNumbers";
const BUSINESS_SLUGS_COLLECTION = "businessSlugs";

const PIONEER_PLAN_NAME = "Pioneer";
const PIONEER_LISTING_YEARS = 1;
export const BUSINESS_SLUG_UNAVAILABLE_MESSAGE =
  "That public profile link is already taken. Please make the Business Name more specific, for example by adding a branch, town, or location.";

const MAX_ACCOUNT_NUMBER_GENERATION_ATTEMPTS = 25;
const MAX_SHORT_DESCRIPTION_LENGTH = 500;

/* =====================================================
   FIELD SAFETY
===================================================== */

const ADMIN_ONLY_BUSINESS_FIELDS = new Set([
  "accountNumber",
  "accountNumberCreatedAt",

  "pioneerSlotId",

  "subscriptionId",
  "subscriptionRef",
  "approvedAt",
  "approvedBy",
  "approvedByUid",

  "publishedAt",
  "publishedBy",
  "publishedByUid",

  "rejectedAt",
  "rejectedBy",
  "rejectedByUid",
  "rejectionReason",

  "changesRequestedAt",
  "changesRequestedBy",
  "changesRequestedByUid",
  "changesRequestMessage",

  "moderationStatus",
  "moderationNotes",
  "adminNotes",

  "isVerified",
  "verifiedAt",
  "verifiedBy",
  "verifiedByUid",

  "isFeatured",
  "featured",
  "featuredAt",
  "featuredUntil",
  "featuredBy",
  "featuredByUid",
  "featuredRank",

  "isClaimed",
  "claim",
  "claimedAt",
  "claimedBy",
  "claimedByUid",
  "claimExpiresAt",
  "claimExpiresMs",
  "claimToken",

  "deletedAt",
  "deletedBy",
  "deletedByUid",
  "ownerDeletedAt",
  "ownerDeletedByUid",

  "deactivatedAt",
  "deactivatedBy",
  "deactivatedByUid",

  "reactivatedAt",
  "reactivatedBy",
  "reactivatedByUid",

  "withdrawnAt",
  "withdrawnBy",
  "withdrawnByUid",

  "subscriptionType",
  "subscriptionPlan",
  "subscriptionStatus",
  "subscriptionStartedAt",
  "subscriptionExpiresAt",
  "subscriptionPaidAt",
  "subscriptionPaymentId",
  "subscriptionPaymentRef",
  "subscriptionOverride",
  "subscriptionOverrideReason",
  "subscriptionOverrideAt",
  "subscriptionOverrideBy",
  "subscriptionOverrideByUid",

  "expiresAt",
  "validUntil",
  "endsAt",
  "paidAt",
  "paidUntil",

  "paymentStatus",
  "paymentProvider",
  "paymentMethod",
  "paymentAmount",
  "paymentCurrency",
  "paymentReference",
  "paymentReceipt",
  "paymentReceiptNumber",
  "paymentPhone",
  "paymentVerifiedAt",
  "paymentVerifiedBy",
  "paymentVerifiedByUid",

  "billingStatus",
  "billingPlan",
  "billingCycle",
  "billingStartedAt",
  "billingEndsAt",
  "billingExpiresAt",

  "mpesaCheckoutRequestId",
  "mpesaMerchantRequestId",
  "mpesaReceiptNumber",
  "mpesaPhoneNumber",
  "mpesaAmount",
  "mpesaResultCode",
  "mpesaResultDesc",
  "mpesaPaidAt",
  "mPesaCheckoutRequestId",
  "mPesaMerchantRequestId",
  "mPesaReceiptNumber",
  "mPesaPhoneNumber",
  "mPesaAmount",
  "mPesaResultCode",
  "mPesaResultDesc",
  "mPesaPaidAt",
]);

const APPROVED_OWNER_SAFE_UPDATE_FIELDS = new Set([
  "ownerName",
  "ownerPhone",

  "businessName",
  "name",

  "phone",
  "email",
  "businessEmail",

  "businessType",

  "website",
  "websiteUrl",
  "whatsappCtaType",
  "whatsappChatNumber",
  "whatsappCatalogUrl",

  "facebook",
  "facebookUrl",
  "instagram",
  "instagramUrl",
  "twitter",
  "twitterUrl",
  "x",
  "xUrl",
  "tiktok",
  "tiktokUrl",
  "youtube",
  "youtubeUrl",

  "description",
  "shortDescription",

  "addressLine",
  "cityLine",

  "category",
  "categoryId",
  "categoryName",
  "categoryNameNormalized",
  "categoryStatus",

  "subCategory",
  "subCategoryText",
  "subcategoryId",
  "subcategoryName",
  "subcategoryNameNormalized",
  "subcategoryStatus",

  "countyId",
  "countyName",
  "cityId",
  "cityName",
  "citySuggestionId",
  "citySuggestionName",
  "areaId",
  "areaName",
  "areaSuggestionId",
  "areaSuggestionName",

  "location",
  "locationPin",
  "hasConfirmedLocationPin",

  "items",
  "itemsTitle",

  "businessHours",

  "logoUrl",
  "logoKind",
  "logoFit",
  "coverUrl",

  "needsAdminReview",
  "lastOwnerEditAt",
  "updatedAt",
]);

const OWNER_SELF_UPDATE_ALLOWED = new Set([
  "displayName",
  "name",
  "fullName",
  "photoURL",
  "phone",
  "phoneNumber",
  "updatedAt",
  "lastLoginAt",
  "acceptedTermsAt",
  "termsAcceptedAt",
  "onboardingStep",
  "onboardingCompleted",
  "profileComplete",
]);

const LOCATION_SOURCE_FIELDS = [
  "location",

  "countyId",
  "countyName",

  "cityId",
  "cityName",
  "citySuggestionId",
  "citySuggestionName",

  "areaId",
  "areaName",
  "areaSuggestionId",
  "areaSuggestionName",
];

const OWNER_DRAFT_EDITABLE_BUSINESS_FIELDS = new Set([
  "ownerName",
  "ownerPhone",

  "businessName",
  "phone",
  "email",
  "businessEmail",

  "name",
  "slug",

  "businessType",

  "website",
  "websiteUrl",
  "whatsappCtaType",
  "whatsappChatNumber",
  "whatsappCatalogUrl",

  "facebook",
  "facebookUrl",
  "instagram",
  "instagramUrl",
  "twitter",
  "twitterUrl",
  "x",
  "xUrl",
  "tiktok",
  "tiktokUrl",
  "youtube",
  "youtubeUrl",

  "description",
  "shortDescription",

  "addressLine",
  "cityLine",

  "category",
  "categoryId",
  "categoryName",
  "categoryNameNormalized",
  "categoryStatus",

  "subCategory",
  "subCategoryText",
  "subcategoryId",
  "subcategoryName",
  "subcategoryNameNormalized",
  "subcategoryStatus",

  "countyId",
  "countyName",
  "cityId",
  "cityName",
  "citySuggestionId",
  "citySuggestionName",
  "areaId",
  "areaName",
  "areaSuggestionId",
  "areaSuggestionName",

  "location",
  "locationPin",
  "hasConfirmedLocationPin",

  "items",
  "itemsTitle",

  "businessHours",

  "logoUrl",
  "logoKind",
  "logoFit",
  "coverUrl",

  "updatedAt",
]);

/* =====================================================
   BASIC HELPERS
===================================================== */

function requireCurrentUser(ownerId?: string) {
  const user = auth.currentUser;

  if (!user) {
    throw new Error("You must be signed in.");
  }

  if (ownerId && user.uid !== ownerId) {
    throw new Error("Owner ID does not match signed-in user.");
  }

  return user;
}

function cleanObject<T extends Record<string, unknown>>(value: T): T {
  const next: Record<string, unknown> = {};

  for (const [key, val] of Object.entries(value)) {
    if (val === undefined) continue;
    next[key] = val;
  }

  return next as T;
}

function cleanText(value: unknown, fallback = "") {
  if (typeof value !== "string") return fallback;
  return value.trim();
}

function safeFirebaseErrorCode(error: unknown) {
  if (!error || typeof error !== "object" || !("code" in error)) return "";

  return String((error as { code?: unknown }).code ?? "")
    .trim()
    .slice(0, 80);
}

function logSafeApplicationError(event: string, error: unknown) {
  const code = safeFirebaseErrorCode(error);

  if (code) {
    console.warn(event, { code });
    return;
  }

  console.warn(event);
}

function cleanNullableText(value: unknown) {
  const text = cleanText(value);
  return text || null;
}

function cleanPhone(value: unknown, fallback = "+254") {
  const phone = cleanText(value);
  return phone || fallback;
}

function cleanStatus(value: unknown) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function normalizeBusinessStatus(value: unknown): BusinessStatus {
  if (
    value === "draft" ||
    value === "pending" ||
    value === "approved" ||
    value === "rejected" ||
    value === "changes_requested"
  ) {
    return value;
  }

  return "draft";
}

function readRulesBusinessStatus(data: Record<string, unknown>) {
  return cleanText(data.status ?? data.triageStatus);
}

function ownerCanEditDraftLikeStatus(status: string) {
  return (
    status === "draft" ||
    status === "rejected" ||
    status === "changes_requested"
  );
}

function getSortedKeys(data: Record<string, unknown>) {
  return Object.keys(data).sort();
}

function getDisallowedOwnerDraftKeys(patch: Record<string, unknown>) {
  return getSortedKeys(patch).filter(
    (key) => !OWNER_DRAFT_EDITABLE_BUSINESS_FIELDS.has(key)
  );
}

function readNumber(
  data: Record<string, unknown>,
  keys: string[],
  fallback = 0
) {
  for (const key of keys) {
    const value = data[key];

    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
  }

  return fallback;
}

function normalizeBusinessId(value: unknown): string | null {
  if (typeof value !== "string") return null;

  const text = value.trim();

  return text ? text : null;
}

function toMillis(value: unknown): number | null {
  if (!value) return null;

  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (
    typeof value === "object" &&
    "toMillis" in value &&
    typeof (value as { toMillis?: unknown }).toMillis === "function"
  ) {
    return (value as { toMillis: () => number }).toMillis();
  }

  if (
    typeof value === "object" &&
    "toDate" in value &&
    typeof (value as { toDate?: unknown }).toDate === "function"
  ) {
    return (value as { toDate: () => Date }).toDate().getTime();
  }

  return null;
}

function addYears(value: Date, years: number) {
  const next = new Date(value);
  next.setFullYear(next.getFullYear() + years);

  return next;
}

function addPioneerYear(value: unknown) {
  const ms = toMillis(value);

  if (ms === null) return null;

  return addYears(new Date(ms), PIONEER_LISTING_YEARS);
}

function sanitizeOptionalText(value: unknown) {
  if (typeof value !== "string") return null;

  const text = value.trim();

  return text || null;
}

function sanitizeLocationPin(value: unknown): BusinessLocationPin {
  if (!value || typeof value !== "object") return null;

  const pin = value as Record<string, unknown>;

  const lat = pin.lat;
  const lng = pin.lng;
  const accuracyMeters = pin.accuracyMeters;
  const confirmedByOwner = pin.confirmedByOwner;

  if (typeof lat !== "number" || !Number.isFinite(lat)) return null;
  if (typeof lng !== "number" || !Number.isFinite(lng)) return null;

  return {
    lat,
    lng,
    accuracyMeters:
      typeof accuracyMeters === "number" && Number.isFinite(accuracyMeters)
        ? accuracyMeters
        : null,
    source: "current_location",
    confirmedByOwner: !!confirmedByOwner,

    placeName: sanitizeOptionalText(pin.placeName),
    roadName: sanitizeOptionalText(pin.roadName),
    landmarkName: sanitizeOptionalText(pin.landmarkName),
    plusCode: sanitizeOptionalText(pin.plusCode),
    formattedAddress: sanitizeOptionalText(pin.formattedAddress),
  };
}

function getPlanNameFromData(data: Record<string, unknown>, fallback: string) {
  return (
    cleanText(data.planName) ||
    cleanText(data.subscriptionType) ||
    cleanText(data.plan) ||
    cleanText(data.type) ||
    fallback
  );
}

function getFirstDateValue(data: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = data[key];

    if (toMillis(value) !== null) {
      return value;
    }
  }

  return null;
}

function getPioneerExpiryFromData(data: Record<string, unknown>) {
  const explicitExpiry = getFirstDateValue(data, [
    "endsAt",
    "expiresAt",
    "validUntil",
    "subscriptionExpiresAt",
  ]);

  if (explicitExpiry) {
    return explicitExpiry;
  }

  const startValue = getFirstDateValue(data, [
    "usedAt",
    "startedAt",
    "createdAt",
    "subscriptionStartedAt",
  ]);

  return addPioneerYear(startValue);
}

export function buildBusinessPublicCardLink(
  slug?: string | null,
  baseUrl = WEB_BASE_URL
) {
  const cleanBase = normalizeWebBaseUrl(baseUrl);

  const cleanSlug = String(slug || "")
    .trim()
    .replace(/^\/+/g, "")
    .replace(/^p\//i, "");

  return cleanSlug ? `${cleanBase}/p/${cleanSlug}` : cleanBase;
}

export function buildPublicLocationLine(params: {
  countyName?: string | null;
  cityName?: string | null;
  areaName?: string | null;
}) {
  const county = cleanText(params.countyName);
  const city = cleanText(params.cityName);
  const area = cleanText(params.areaName);

  if (area && city) return `${area}, ${city}`;
  if (city && county) return `${city}, ${county}`;
  if (city) return city;
  if (county) return county;

  return "";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function hasAnyLocationSource(patch: Record<string, unknown>) {
  return LOCATION_SOURCE_FIELDS.some((key) =>
    Object.prototype.hasOwnProperty.call(patch, key)
  );
}

function readLocationValue(
  next: Record<string, unknown>,
  rawLocation: RawLocationPatch,
  key: keyof RawLocationPatch
) {
  if (Object.prototype.hasOwnProperty.call(rawLocation, key)) {
    return rawLocation[key];
  }

  return next[key as string];
}

function normalizeLocationFields(next: Record<string, unknown>) {
  const hasLocationSource = hasAnyLocationSource(next);

  if (!hasLocationSource) {
    if (Object.prototype.hasOwnProperty.call(next, "cityLine")) {
      delete next.cityLine;
    }

    return;
  }

  const rawLocation = isRecord(next.location)
    ? (next.location as RawLocationPatch)
    : {};

  const countyId = cleanNullableText(
    readLocationValue(next, rawLocation, "countyId")
  );
  const countyName = cleanNullableText(
    readLocationValue(next, rawLocation, "countyName")
  );

  const cityId = cleanNullableText(
    readLocationValue(next, rawLocation, "cityId")
  );
  const cityName = cleanNullableText(
    readLocationValue(next, rawLocation, "cityName")
  );

  const citySuggestionId = cleanNullableText(
    readLocationValue(next, rawLocation, "citySuggestionId")
  );
  const citySuggestionName = cleanNullableText(
    readLocationValue(next, rawLocation, "citySuggestionName")
  );

  const areaId = cleanNullableText(
    readLocationValue(next, rawLocation, "areaId")
  );
  const areaName = cleanNullableText(
    readLocationValue(next, rawLocation, "areaName")
  );

  const areaSuggestionId = cleanNullableText(
    readLocationValue(next, rawLocation, "areaSuggestionId")
  );
  const areaSuggestionName = cleanNullableText(
    readLocationValue(next, rawLocation, "areaSuggestionName")
  );

  const publicCityName = cityName || citySuggestionName;
  const publicAreaName = areaName || areaSuggestionName;

  const generatedCityLine = buildPublicLocationLine({
    countyName,
    cityName: publicCityName,
    areaName: publicAreaName,
  });

  next.countyId = countyId;
  next.countyName = countyName;

  next.cityId = cityId;
  next.cityName = cityName;
  next.citySuggestionId = citySuggestionId;
  next.citySuggestionName = citySuggestionName;

  next.areaId = areaId;
  next.areaName = areaName;
  next.areaSuggestionId = areaSuggestionId;
  next.areaSuggestionName = areaSuggestionName;

  next.cityLine = generatedCityLine;

  next.location = {
    countyId,
    countyName,

    cityId,
    cityName,
    citySuggestionId,
    citySuggestionName,

    areaId,
    areaName,
    areaSuggestionId,
    areaSuggestionName,
  };
}

/* =====================================================
   BUSINESS ACCOUNT NUMBER HELPERS
===================================================== */

async function reserveUniqueBusinessAccountNumber(
  transaction: Transaction,
  businessId: string,
  ownerId: string
) {
  for (
    let attempt = 0;
    attempt < MAX_ACCOUNT_NUMBER_GENERATION_ATTEMPTS;
    attempt += 1
  ) {
    const accountNumber = generateBusinessAccountNumber();

    if (!isValidBusinessAccountNumber(accountNumber)) {
      continue;
    }

    const accountRef = doc(
      db,
      BUSINESS_ACCOUNT_NUMBERS_COLLECTION,
      accountNumber
    );

    const accountSnap = await transaction.get(accountRef);

    if (accountSnap.exists()) {
      continue;
    }

    transaction.set(accountRef, {
      accountNumber,
      businessId,
      ownerId,
      createdAt: serverTimestamp(),
    });

    return accountNumber;
  }

  throw new Error(
    "Could not generate a unique business account number. Please try again."
  );
}

/* =====================================================
   ENTITLEMENT / PIONEER HELPERS
===================================================== */

function isEntitlementUsable(data: EntitlementData) {
  const status = cleanStatus(data.status);

  if (
    status &&
    !["active", "trialing", "paid", "approved", "enabled"].includes(status)
  ) {
    return {
      allowed: false,
      reason: "Your listing plan is not active.",
      maxBusinesses: 0,
      usedBusinesses: 0,
    };
  }

  if (data.isActive === false || data.active === false) {
    return {
      allowed: false,
      reason: "Your listing plan is not active.",
      maxBusinesses: 0,
      usedBusinesses: 0,
    };
  }

  const expiresAtMs =
    toMillis(data.endsAt) ??
    toMillis(data.expiresAt) ??
    toMillis(data.validUntil);

  if (expiresAtMs !== null && expiresAtMs <= Date.now()) {
    return {
      allowed: false,
      reason: "Your listing plan has expired.",
      maxBusinesses: 0,
      usedBusinesses: 0,
    };
  }

  const source = data as Record<string, unknown>;

  const maxBusinesses = readNumber(source, [
    "maxBusinesses",
    "businessLimit",
    "maxListings",
    "listingLimit",
  ]);

  const usedBusinesses = readNumber(source, [
    "usedBusinesses",
    "usedBusinessCount",
    "businessesUsed",
    "usedListings",
  ]);

  if (maxBusinesses <= 0) {
    return {
      allowed: false,
      reason: "Your listing plan does not include any business listings.",
      maxBusinesses,
      usedBusinesses,
    };
  }

  if (usedBusinesses >= maxBusinesses) {
    return {
      allowed: false,
      reason: "Your active listing plan has already been used.",
      maxBusinesses,
      usedBusinesses,
    };
  }

  return {
    allowed: true,
    maxBusinesses,
    usedBusinesses,
  };
}

function isRawPioneerSlotUsable(data: Record<string, unknown>) {
  const businessId = normalizeBusinessId(data.businessId);
  const status = normalizePioneerSlotStatus(data);
  const expiryMs = toMillis(getPioneerExpiryFromData(data));

  const maxBusinesses = readNumber(
    data,
    ["maxBusinesses", "businessLimit", "maxListings", "listingLimit"],
    1
  );

  const usedBusinesses = readNumber(
    data,
    ["usedBusinesses", "usedBusinessCount", "businessesUsed", "usedListings"],
    businessId ? 1 : 0
  );

  return (
    (status === "available" || status === "active") &&
    (expiryMs === null || expiryMs > Date.now()) &&
    !businessId &&
    usedBusinesses < maxBusinesses
  );
}

async function readFirstExistingDoc(refs: DocumentReference<DocumentData>[]) {
  for (const refDoc of refs) {
    const snap = await getDoc(refDoc);

    if (snap.exists()) {
      return {
        id: snap.id,
        data: snap.data(),
      };
    }
  }

  return null;
}

async function readDirectEntitlement(ownerId: string) {
  const canonicalEntitlement = await getActiveEntitlement(ownerId);

  if (canonicalEntitlement) {
    return {
      id: canonicalEntitlement.id,
      data: canonicalEntitlement as unknown as DocumentData,
    };
  }

  return await readFirstExistingDoc([
    doc(db, "ownerEntitlements", ownerId),
    doc(db, "entitlements", ownerId),
    doc(db, "owners", ownerId, "entitlements", "active"),
  ]);
}

async function readOwnerPioneerSlot(ownerId: string) {
  const snap = await getDoc(doc(db, PIONEER_SLOTS_COLLECTION, ownerId));

  if (!snap.exists()) {
    return null;
  }

  return {
    id: snap.id,
    data: snap.data() as Record<string, unknown>,
  };
}

export async function readOwnerListingSubscriptionSummary(
  ownerId: string
): Promise<OwnerListingSubscriptionSummary> {
  requireCurrentUser(ownerId);

  const entitlement = await readDirectEntitlement(ownerId);

  if (entitlement) {
    const data = entitlement.data as Record<string, unknown>;
    const usable = isEntitlementUsable(data as EntitlementData);

    return {
      source: "entitlement",
      id: entitlement.id,
      planName: getPlanNameFromData(data, "Listing Plan"),
      status:
        cleanStatus(data.status) || (usable.allowed ? "active" : "inactive"),
      active: usable.allowed,
      maxBusinesses: usable.maxBusinesses,
      usedBusinesses: usable.usedBusinesses,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      startedAt: data.startedAt || data.startsAt,
      expiresAt: data.expiresAt,
      validUntil: data.validUntil,
      endsAt: data.endsAt,
    };
  }

  const pioneerSlot = await readOwnerPioneerSlot(ownerId);

  if (pioneerSlot) {
    const data = pioneerSlot.data;

    const rawStatus = normalizePioneerSlotStatus(data);

    const startedAt =
      data.usedAt || data.startedAt || data.createdAt || data.updatedAt || null;

    const expiryValue = getPioneerExpiryFromData(data);
    const expiryMs = toMillis(expiryValue);
    const expired = expiryMs !== null && expiryMs <= Date.now();

    const status =
      rawStatus === "unknown" ? "unknown" : expired ? "expired" : rawStatus;

    return {
      source: "pioneer",
      id: pioneerSlot.id,
      planName: PIONEER_PLAN_NAME,
      status,
      active:
        !expired &&
        (status === "used" || status === "active" || status === "available"),
      businessId: normalizeBusinessId(data.businessId),
      maxBusinesses: readNumber(data, ["maxBusinesses"], 1),
      usedBusinesses: readNumber(
        data,
        ["usedBusinesses"],
        rawStatus === "used" ? 1 : 0
      ),
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      startedAt,
      expiresAt: expiryValue,
      validUntil: data.validUntil || expiryValue,
      endsAt: data.endsAt || expiryValue,
    };
  }

  return {
    source: "none",
    planName: "No plan",
    status: "none",
    active: false,
  };
}

async function canCreateBusinessDraftForOnboarding(
  ownerId: string
): Promise<CreateBusinessDraftPermission> {
  requireCurrentUser(ownerId);

  const entitlement = await readDirectEntitlement(ownerId);

  if (entitlement) {
    const result = isEntitlementUsable(entitlement.data as EntitlementData);

    if (result.allowed) {
      return {
        allowed: true,
        source: "entitlement",
        entitlementId: entitlement.id,
        maxBusinesses: result.maxBusinesses,
        usedBusinesses: result.usedBusinesses,
      };
    }
  }

  const pioneerSlot = await readOwnerPioneerSlot(ownerId);

  if (pioneerSlot && isRawPioneerSlotUsable(pioneerSlot.data)) {
    return {
      allowed: true,
      source: "pioneer",
      pioneerSlotId: pioneerSlot.id,
      maxBusinesses: readNumber(pioneerSlot.data, ["maxBusinesses"], 1),
      usedBusinesses: readNumber(pioneerSlot.data, ["usedBusinesses"], 0),
    };
  }

  return {
    allowed: false,
    reason:
      "You need an active listing plan or an available Pioneer Business slot before creating a business profile.",
  };
}

/* =====================================================
   BUSINESS READ / DEFAULTS
===================================================== */

async function readExistingOwnerBusiness(
  ownerId: string
): Promise<ExistingOwnerBusiness | null> {
  const q = query(
    collection(db, BUSINESS_COLLECTION),
    where("ownerId", "==", ownerId),
    limit(50)
  );

  const snap = await getDocs(q);

  const businesses = snap.docs
    .map((docSnap) => ({
      id: docSnap.id,
      ...(docSnap.data() as Omit<ExistingOwnerBusiness, "id">),
    }))
    .filter((business) => business.isDeleted !== true)
    .sort((a, b) => {
      const aMs = toMillis(a.updatedAt) ?? toMillis(a.createdAt) ?? 0;
      const bMs = toMillis(b.updatedAt) ?? toMillis(b.createdAt) ?? 0;

      return bMs - aMs;
    });

  return selectOwnerLifecycleBusiness(businesses);
}

function getBusinessDefaults(
  ownerId: string,
  seed: CreateDraftBusinessInput = {},
  permission?: CreateBusinessDraftPermission
) {
  const user = auth.currentUser;

  const ownerName =
    cleanText(seed.ownerName) || cleanText(user?.displayName) || "";
  const businessName = cleanText(seed.businessName);
  const mobileNumber = cleanPhone(seed.mobileNumber);

  return cleanObject({
    ownerId,
    createdBy: ownerId,
    createdByUid: ownerId,

    status: "draft" as BusinessStatus,
    isPublished: false,
    isDeleted: false,
    isDeactivated: false,

    isClaimed: false,
    claim: null,

    onboardingStep: "basics",
    onboardingCompleted: false,
    submittedAt: null,

    creationSource: permission?.source ?? "unknown",

    ownerName,
    ownerPhone: mobileNumber,

    name: businessName,
    slug: "",
    phone: mobileNumber,

    businessType: null as BusinessType,

    addressLine: "",
    cityLine: "",
    description: "",
    shortDescription: "",

    category: "",
    categoryId: null,
    categoryName: "",
    categoryNameNormalized: "",
    categoryStatus: "approved" as const,

    subCategory: [] as string[],
    subCategoryText: "",
    subcategoryId: null,
    subcategoryName: "",
    subcategoryNameNormalized: "",
    subcategoryStatus: "approved" as const,

    countyId: null,
    countyName: "",
    cityId: null,
    cityName: "",
    areaId: null,
    areaName: "",

    location: {
      countyId: null,
      countyName: null,
      cityId: null,
      cityName: null,
      citySuggestionId: null,
      citySuggestionName: null,
      areaId: null,
      areaName: null,
      areaSuggestionId: null,
      areaSuggestionName: null,
    },

    locationPin: null as BusinessLocationPin,
    hasConfirmedLocationPin: false,

    items: [] as BusinessItem[],

    businessHours: createDefaultBusinessHours(),

    logoUrl: "",
    logoKind: "logo" as const,
    logoFit: "contain" as const,
    coverUrl: "",

    website: "",
    email: user?.email ?? "",
    whatsappCtaType: "chat",
    whatsappChatNumber: "",
    whatsappCatalogUrl: "",

    facebook: "",
    instagram: "",
    twitter: "",
    tiktok: "",
    youtube: "",
  });
}

/* =====================================================
   PATCH NORMALIZATION
===================================================== */

function normalizeOwnerPatch(patch: Record<string, unknown>) {
  const next: Record<string, unknown> = {};

  if (typeof patch.name === "string") {
    const name = patch.name.trim();
    next.name = name;
    next.fullName = name;
    next.displayName = name;
  }

  if (typeof patch.fullName === "string") {
    const fullName = patch.fullName.trim();
    next.name = fullName;
    next.fullName = fullName;
    next.displayName = fullName;
  }

  if (typeof patch.displayName === "string") {
    const displayName = patch.displayName.trim();
    next.name = displayName;
    next.displayName = displayName;
  }

  if (typeof patch.phone === "string") {
    next.phone = patch.phone.trim();
    next.phoneNumber = patch.phone.trim();
  }

  if (typeof patch.phoneNumber === "string") {
    next.phoneNumber = patch.phoneNumber.trim();
    next.phone = patch.phoneNumber.trim();
  }

  if (typeof patch.photoURL === "string") {
    next.photoURL = patch.photoURL;
  }

  if (typeof patch.onboardingStep === "string") {
    next.onboardingStep = patch.onboardingStep;
  }

  if (typeof patch.onboardingCompleted === "boolean") {
    next.onboardingCompleted = patch.onboardingCompleted;
  }

  if (typeof patch.profileComplete === "boolean") {
    next.profileComplete = patch.profileComplete;
  }

  next.updatedAt = serverTimestamp();

  const safe: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(next)) {
    if (OWNER_SELF_UPDATE_ALLOWED.has(key)) {
      safe[key] = value;
    }
  }

  return safe;
}

function normalizeBusinessPatch(patch: Record<string, unknown>) {
  const next = { ...patch } as Record<string, unknown>;

  for (const key of ADMIN_ONLY_BUSINESS_FIELDS) {
    delete next[key];
  }

  delete next.claim;

  delete next.ownerId;
  delete next.createdBy;
  delete next.createdByUid;
  delete next.userId;
  delete next.uid;

  if (next.isDeleted === true) {
    delete next.isDeleted;
  }

  if (next.isDeactivated === true) {
    delete next.isDeactivated;
  }

  if (Object.prototype.hasOwnProperty.call(next, "shortDescription")) {
    next.shortDescription =
      typeof next.shortDescription === "string"
        ? next.shortDescription.trim().slice(0, MAX_SHORT_DESCRIPTION_LENGTH)
        : "";
  }

  if (Object.prototype.hasOwnProperty.call(next, "whatsappCtaType")) {
    next.whatsappCtaType = normalizeWhatsAppCtaType(next.whatsappCtaType);
  }

  if (Object.prototype.hasOwnProperty.call(next, "whatsappChatNumber")) {
    next.whatsappChatNumber =
      normalizeWhatsAppChatNumber(next.whatsappChatNumber as string | null) ||
      cleanText(next.whatsappChatNumber);
  }

  if (Object.prototype.hasOwnProperty.call(next, "whatsappCatalogUrl")) {
    next.whatsappCatalogUrl = cleanText(next.whatsappCatalogUrl);
  }

  normalizeLocationFields(next);

  const businessType = Object.prototype.hasOwnProperty.call(
    next,
    "businessType"
  )
    ? ((next.businessType ?? null) as BusinessType)
    : undefined;

  if (Object.prototype.hasOwnProperty.call(next, "locationPin")) {
    const normalizedPin = sanitizeLocationPin(next.locationPin);

    next.locationPin = normalizedPin;
    next.hasConfirmedLocationPin = normalizedPin?.confirmedByOwner ?? false;
  }

  if (businessType === "freelancer" || businessType === "online_only") {
    next.addressLine = "";
    next.locationPin = null;
    next.hasConfirmedLocationPin = false;
  }

  if (
    businessType === "physical_shop" &&
    Object.prototype.hasOwnProperty.call(next, "hasConfirmedLocationPin") &&
    !next.locationPin
  ) {
    next.hasConfirmedLocationPin = false;
  }

  if (Object.prototype.hasOwnProperty.call(next, "businessHours")) {
    next.businessHours = sanitizeBusinessHours(next.businessHours);
  }

  next.updatedAt = serverTimestamp();

  return cleanObject(next);
}

function filterApprovedBusinessSafePatch(patch: Record<string, unknown>) {
  const safe: Record<string, unknown> = {};
  const blocked: string[] = [];

  for (const [key, value] of Object.entries(patch)) {
    if (APPROVED_OWNER_SAFE_UPDATE_FIELDS.has(key)) {
      safe[key] = value;
    } else {
      blocked.push(key);
    }
  }

  return { safe, blocked };
}

/* =====================================================
   OWNER ACCOUNT
===================================================== */

export async function ensureOwnerDoc(userArg?: User | string | null) {
  const currentUser = auth.currentUser;

  const user =
    typeof userArg === "object" && userArg !== null ? userArg : currentUser;

  const expectedOwnerId = typeof userArg === "string" ? userArg : user?.uid;

  if (!user) {
    throw new Error("No authenticated user found while preparing owner account.");
  }

  if (expectedOwnerId && user.uid !== expectedOwnerId) {
    throw new Error("Owner ID does not match signed-in user.");
  }

  const ownerId = user.uid;
  const ownerRef = doc(db, "owners", ownerId);
  const ownerSnap = await getDoc(ownerRef);

  const displayName = user.displayName ?? "";
  const email = user.email ?? "";
  const phoneNumber = user.phoneNumber ?? "";
  const photoURL = user.photoURL ?? "";

  const safeCreateData = cleanObject({
    uid: ownerId,
    userId: ownerId,

    email,

    displayName,
    name: displayName,
    fullName: displayName,

    phone: phoneNumber,
    phoneNumber,

    photoURL,

    onboardingStep: "basics",
    onboardingCompleted: false,
    profileComplete: false,

    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastLoginAt: serverTimestamp(),
  });

  const safeUpdateData = cleanObject({
    uid: ownerId,
    userId: ownerId,

    email,

    displayName,
    name: displayName,
    fullName: displayName,

    phone: phoneNumber,
    phoneNumber,

    photoURL,

    updatedAt: serverTimestamp(),
    lastLoginAt: serverTimestamp(),
  });

  if (!ownerSnap.exists()) {
    await setDoc(ownerRef, safeCreateData);
    return;
  }

  const existingOwner = ownerSnap.data() as Record<string, unknown>;
  for (const field of ["uid", "userId"] as const) {
    const existing = existingOwner[field];
    const isMissing =
      existing === undefined ||
      existing === null ||
      (typeof existing === "string" && existing.trim() === "");
    if (!isMissing && existing !== ownerId) {
      throw new Error("Owner account identifier requires support review.");
    }
  }

  await setDoc(ownerRef, safeUpdateData, { merge: true });
}

export async function readOwnerLegalAcceptance(
  ownerId: string
): Promise<OwnerLegalAcceptance | null> {
  requireCurrentUser(ownerId);

  const snap = await getDoc(doc(db, "owners", ownerId));

  if (!snap.exists()) {
    return null;
  }

  const ownerDocument = snap.data() as Record<string, unknown>;
  if (
    !hasCurrentOwnerLegalAcceptance({
      ownerId,
      ownerDocument,
      nowMs: Date.now(),
    })
  ) {
    return null;
  }

  const legalAccepted = ownerDocument.legalAccepted;

  return isRecord(legalAccepted)
    ? (legalAccepted as OwnerLegalAcceptance)
    : null;
}

export function hasAcceptedCurrentLegalVersion(
  ownerId: string,
  ownerDocument: unknown,
  nowMs = Date.now()
) {
  return hasCurrentOwnerLegalAcceptance({ ownerId, ownerDocument, nowMs });
}

export async function saveCurrentOwnerLegalAcceptance(ownerId: string) {
  requireCurrentUser(ownerId);

  await ensureOwnerDoc(ownerId);
  const ownerRef = doc(db, "owners", ownerId);

  const legalAccepted: OwnerLegalAcceptance = {
    version: LEGAL_VERSION,
    termsVersion: LEGAL_VERSION,
    privacyVersion: LEGAL_VERSION,
    refundVersion: LEGAL_VERSION,
    listingGuidelinesVersion: LEGAL_VERSION,
    acceptedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(
    ownerRef,
    {
      legalAccepted,
    },
    { merge: true }
  );
}

export async function patchOwner(
  ownerId: string,
  patch: Record<string, unknown>
) {
  requireCurrentUser(ownerId);

  await ensureOwnerDoc(ownerId);

  const safePatch = normalizeOwnerPatch(patch);

  if (Object.keys(safePatch).length === 0) {
    return;
  }

  try {
    await updateDoc(doc(db, "owners", ownerId), safePatch);
  } catch (error) {
    console.warn("Owner profile update failed.");
    throw error;
  }
}

export async function persistOwnerOnboardingStep(step: OwnerOnboardingStep) {
  try {
    const user = requireCurrentUser();

    await patchOwner(user.uid, { onboardingStep: step });
    return true;
  } catch (error) {
    logSafeApplicationError("Owner onboarding step persistence failed.", error);
    return false;
  }
}

export async function readOwnerOnboardingStep(ownerId: string) {
  requireCurrentUser(ownerId);

  const ownerSnap = await getDoc(doc(db, "owners", ownerId));

  return ownerSnap.exists() ? ownerSnap.data().onboardingStep : null;
}

/* =====================================================
   BUSINESS CREATE / READ / PATCH
===================================================== */

export async function createDraftBusiness(
  ownerId: string,
  seed: CreateDraftBusinessInput = {}
) {
  requireCurrentUser(ownerId);

  await ensureOwnerDoc(ownerId);

  const existingBusiness = await readExistingOwnerBusiness(ownerId);

  if (existingBusiness?.id) {
    if (
      existingBusiness.status === "draft" ||
      existingBusiness.status === "changes_requested" ||
      existingBusiness.status === "rejected"
    ) {
      return existingBusiness.id;
    }

    throw new Error(
      "You already have a business profile connected to this account. Please manage it from your dashboard."
    );
  }

  const draftPermission = await canCreateBusinessDraftForOnboarding(ownerId);

  if (!draftPermission.allowed) {
    throw new Error(
      draftPermission.reason ??
        "You need an active listing plan before creating a business."
    );
  }

  const businessDefaults = getBusinessDefaults(ownerId, seed, draftPermission);

  try {
    if (draftPermission.source === "pioneer") {
      const refSlot = doc(db, PIONEER_SLOTS_COLLECTION, ownerId);
      const refBusiness = doc(collection(db, BUSINESS_COLLECTION));

      const clientNow = new Date();
      const pioneerExpiresAt = addYears(clientNow, PIONEER_LISTING_YEARS);

      await runTransaction(db, async (transaction) => {
        const slotSnap = await transaction.get(refSlot);

        if (!slotSnap.exists()) {
          throw new Error("Your Pioneer Business slot was not found.");
        }

        const slotData = slotSnap.data() as Record<string, unknown>;

        if (!isRawPioneerSlotUsable(slotData)) {
          throw new Error(
            "This Google account has already used its Pioneer Business listing slot."
          );
        }

        const accountNumber = await reserveUniqueBusinessAccountNumber(
          transaction,
          refBusiness.id,
          ownerId
        );

        const currentUsedBusinesses = readNumber(
          slotData,
          [
            "usedBusinesses",
            "usedBusinessCount",
            "businessesUsed",
            "usedListings",
          ],
          0
        );

        transaction.set(refBusiness, {
          ...businessDefaults,

          accountNumber,
          accountNumberCreatedAt: serverTimestamp(),

          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        transaction.update(refSlot, {
          status: "used",
          businessId: refBusiness.id,
          usedBusinesses: currentUsedBusinesses + 1,
          ownerName: businessDefaults.ownerName,
          businessName: businessDefaults.name,
          mobileNumber: businessDefaults.phone,
          usedAt: serverTimestamp(),
          expiresAt: slotData.expiresAt || pioneerExpiresAt,
          validUntil: slotData.validUntil || pioneerExpiresAt,
          endsAt: slotData.endsAt || pioneerExpiresAt,
          updatedAt: serverTimestamp(),
        });
      });

      return refBusiness.id;
    }

    const refBusiness = doc(collection(db, BUSINESS_COLLECTION));

    await runTransaction(db, async (transaction) => {
      const accountNumber = await reserveUniqueBusinessAccountNumber(
        transaction,
        refBusiness.id,
        ownerId
      );

      transaction.set(refBusiness, {
        ...businessDefaults,

        accountNumber,
        accountNumberCreatedAt: serverTimestamp(),

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });

    return refBusiness.id;
  } catch (error) {
    console.warn("Business draft creation failed.");
    throw error;
  }
}

export async function readBusiness(businessId: string) {
  if (!businessId) return null;

  try {
    const snap = await getDoc(doc(db, BUSINESS_COLLECTION, businessId));

    if (!snap.exists()) return null;

    return {
      id: snap.id,
      ...(snap.data() as Record<string, unknown>),
    };
  } catch (error) {
    logSafeApplicationError("Business read failed.", error);
    throw error;
  }
}

export async function patchBusiness(
  businessId: string,
  patch: Record<string, unknown>
) {
  if (!businessId) {
    throw new Error("patchBusiness: missing businessId");
  }

  const user = requireCurrentUser();

  const refDoc = doc(db, BUSINESS_COLLECTION, businessId);
  const safePatch = normalizeBusinessPatch(patch);
  const diagnostics = {
    authUid: user.uid,
    businessId,
    businessPath: `${BUSINESS_COLLECTION}/${businessId}`,
    existingBusinessOwnerId: null as string | null,
    existingBusinessStatus: null as string | null,
    safePayloadKeys: getSortedKeys(safePatch),
    disallowedDraftPayloadKeys: getDisallowedOwnerDraftKeys(safePatch),
    firebaseCode: undefined as unknown,
    firebaseMessage: undefined as unknown,
  };

  try {
    const snap = await getDoc(refDoc);

    if (!snap.exists()) {
      throw new Error("Business not found.");
    }

    const current = snap.data() as Record<string, unknown>;
    diagnostics.existingBusinessOwnerId = cleanText(current.ownerId) || null;
    diagnostics.existingBusinessStatus = readRulesBusinessStatus(current) || null;

    if (current.ownerId !== user.uid) {
      throw new Error("You can only update your own business.");
    }

    if (current.isDeleted === true) {
      throw new Error("This business has been deleted.");
    }

    if (current.isDeactivated === true) {
      throw new Error(
        "This business is deactivated. Reactivate it before editing."
      );
    }

    const currentStatus = readRulesBusinessStatus(current);

    if (Object.keys(safePatch).length === 0) {
      return;
    }

    if (currentStatus === "pending") {
      throw new Error(
        "This business is pending admin review. Withdraw the submission before editing."
      );
    }

    if (currentStatus === "approved") {
      const { safe, blocked } = filterApprovedBusinessSafePatch(safePatch);

      if (blocked.length > 0) {
        throw new Error(
          `This approved business field requires admin review: ${blocked.join(
            ", "
          )}.`
        );
      }

      if (Object.keys(safe).length === 0) {
        return;
      }

      await updateDoc(refDoc, {
        ...safe,
        updatedAt: serverTimestamp(),
        lastOwnerEditAt: serverTimestamp(),
        needsAdminReview: false,
      });
      return;
    }

    if (!ownerCanEditDraftLikeStatus(currentStatus)) {
      throw new Error(
        "This business cannot be edited from onboarding in its current status."
      );
    }

    if (diagnostics.disallowedDraftPayloadKeys.length > 0) {
      throw new Error(
        "This save includes fields that owners cannot update from onboarding."
      );
    }

    await updateDoc(refDoc, safePatch);
  } catch (error) {
    diagnostics.firebaseCode = (error as { code?: unknown })?.code;
    diagnostics.firebaseMessage = (error as { message?: unknown })?.message;

    logSafeApplicationError("Business update failed.", error);
    throw error;
  }
}

function isReservableBusinessSlug(slug: string) {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);
}

function getBusinessSlugReservationRef(slug: string) {
  return doc(db, BUSINESS_SLUGS_COLLECTION, slug);
}

function reservationBelongsToBusiness(
  data: DocumentData,
  businessId: string
) {
  return cleanText(data.businessId) === businessId;
}

type SlugReservationWriteDiagnostics = {
  authUid: string;
  businessId: string;
  businessPath: string;
  slugPath: string;
  oldSlugPath: string | null;
  existingBusinessOwnerId: string | null;
  existingBusinessStatus: string | null;
  safePayloadKeys: string[];
  disallowedPayloadKeys: string[];
  newSlugReservationExists: boolean | null;
  newSlugReservationMatchesBusiness: boolean | null;
  oldSlugReservationExists: boolean | null;
  oldSlugReservationMatchesBusiness: boolean | null;
  plannedWritePaths: string[];
  predictedBlockedPath: string | null;
  predictedBlockReason: string | null;
  firebaseCode?: unknown;
  firebaseMessage?: unknown;
};

function createSlugReservationDiagnostics(args: {
  authUid: string;
  businessId: string;
  cleanSlug: string;
  safePatch: Record<string, unknown>;
}): SlugReservationWriteDiagnostics {
  return {
    authUid: args.authUid,
    businessId: args.businessId,
    businessPath: `${BUSINESS_COLLECTION}/${args.businessId}`,
    slugPath: `${BUSINESS_SLUGS_COLLECTION}/${args.cleanSlug}`,
    oldSlugPath: null,
    existingBusinessOwnerId: null,
    existingBusinessStatus: null,
    safePayloadKeys: getSortedKeys(args.safePatch),
    disallowedPayloadKeys: getDisallowedOwnerDraftKeys(args.safePatch),
    newSlugReservationExists: null,
    newSlugReservationMatchesBusiness: null,
    oldSlugReservationExists: null,
    oldSlugReservationMatchesBusiness: null,
    plannedWritePaths: [],
    predictedBlockedPath: null,
    predictedBlockReason: null,
  };
}

function setPredictedSlugReservationBlock(
  diagnostics: SlugReservationWriteDiagnostics,
  path: string,
  reason: string
) {
  if (!diagnostics.predictedBlockedPath) {
    diagnostics.predictedBlockedPath = path;
    diagnostics.predictedBlockReason = reason;
  }
}

export async function patchBusinessWithSlugReservation(
  businessId: string,
  patch: Record<string, unknown>,
  slug: string
) {
  if (!businessId) {
    throw new Error("patchBusinessWithSlugReservation: missing businessId");
  }

  const cleanSlug = cleanText(slug);

  if (!cleanSlug || !isReservableBusinessSlug(cleanSlug)) {
    throw new Error(
      "Please use a Business Name with letters or numbers so we can create your public profile link."
    );
  }

  const user = requireCurrentUser();
  const refBusiness = doc(db, BUSINESS_COLLECTION, businessId);
  const refNewSlug = getBusinessSlugReservationRef(cleanSlug);
  const safePatch = normalizeBusinessPatch({
    ...patch,
    slug: cleanSlug,
  });
  const diagnostics = createSlugReservationDiagnostics({
    authUid: user.uid,
    businessId,
    cleanSlug,
    safePatch,
  });

  try {
    await runTransaction(db, async (transaction) => {
      const businessSnap = await transaction.get(refBusiness);

      if (!businessSnap.exists()) {
        throw new Error("Business not found.");
      }

      const current = businessSnap.data() as Record<string, unknown>;
      const currentStatus = readRulesBusinessStatus(current);

      diagnostics.existingBusinessOwnerId = cleanText(current.ownerId) || null;
      diagnostics.existingBusinessStatus = currentStatus || null;

      if (current.ownerId !== user.uid) {
        setPredictedSlugReservationBlock(
          diagnostics,
          diagnostics.businessPath,
          "business.ownerId does not match auth uid"
        );
        throw new Error("You can only update your own business.");
      }

      if (current.isDeleted === true) {
        setPredictedSlugReservationBlock(
          diagnostics,
          diagnostics.businessPath,
          "business is deleted"
        );
        throw new Error("This business has been deleted.");
      }

      if (current.isDeactivated === true) {
        setPredictedSlugReservationBlock(
          diagnostics,
          diagnostics.businessPath,
          "business is deactivated"
        );
        throw new Error(
          "This business is deactivated. Reactivate it before editing."
        );
      }

      if (currentStatus === "pending") {
        setPredictedSlugReservationBlock(
          diagnostics,
          diagnostics.businessPath,
          "business status is pending"
        );
        throw new Error(
          "This business is pending admin review. Withdraw the submission before editing."
        );
      }

      if (currentStatus === "approved") {
        setPredictedSlugReservationBlock(
          diagnostics,
          diagnostics.businessPath,
          "business status is approved"
        );
        throw new Error(
          "This approved business field requires admin review: slug."
        );
      }

      if (!ownerCanEditDraftLikeStatus(currentStatus)) {
        setPredictedSlugReservationBlock(
          diagnostics,
          diagnostics.businessPath,
          "business status is not draft, rejected, or changes_requested"
        );
        throw new Error(
          "This business cannot be edited from onboarding in its current status."
        );
      }

      if (diagnostics.disallowedPayloadKeys.length > 0) {
        setPredictedSlugReservationBlock(
          diagnostics,
          diagnostics.businessPath,
          "payload contains fields outside ownerDraftEditableFields"
        );
        throw new Error(
          "This save includes fields that owners cannot update from Basics."
        );
      }

      const currentSlug = cleanText(current.slug);
      const refOldSlug =
        currentSlug &&
        currentSlug !== cleanSlug &&
        isReservableBusinessSlug(currentSlug)
          ? getBusinessSlugReservationRef(currentSlug)
          : null;

      diagnostics.oldSlugPath = refOldSlug
        ? `${BUSINESS_SLUGS_COLLECTION}/${currentSlug}`
        : null;

      const newSlugSnap = await transaction.get(refNewSlug);
      const oldSlugSnap = refOldSlug
        ? await transaction.get(refOldSlug)
        : null;

      diagnostics.newSlugReservationExists = newSlugSnap.exists();
      diagnostics.newSlugReservationMatchesBusiness = newSlugSnap.exists()
        ? reservationBelongsToBusiness(newSlugSnap.data(), businessId)
        : null;
      diagnostics.oldSlugReservationExists = oldSlugSnap?.exists() ?? null;
      diagnostics.oldSlugReservationMatchesBusiness = oldSlugSnap?.exists()
        ? reservationBelongsToBusiness(oldSlugSnap.data(), businessId)
        : null;

      if (newSlugSnap.exists()) {
        const reservation = newSlugSnap.data();

        if (!reservationBelongsToBusiness(reservation, businessId)) {
          throw new Error(BUSINESS_SLUG_UNAVAILABLE_MESSAGE);
        }

        transaction.set(
          refNewSlug,
          {
            businessId,
            ownerId: user.uid,
            createdAt: reservation.createdAt ?? serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        diagnostics.plannedWritePaths.push(diagnostics.slugPath);
      } else {
        transaction.set(refNewSlug, {
          businessId,
          ownerId: user.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        diagnostics.plannedWritePaths.push(diagnostics.slugPath);
      }

      if (Object.keys(safePatch).length === 0) {
        return;
      }

      transaction.update(refBusiness, safePatch);
      diagnostics.plannedWritePaths.push(diagnostics.businessPath);

      if (refOldSlug && oldSlugSnap?.exists()) {
        const oldReservation = oldSlugSnap.data();

        if (reservationBelongsToBusiness(oldReservation, businessId)) {
          transaction.delete(refOldSlug);
          diagnostics.plannedWritePaths.push(
            `${BUSINESS_SLUGS_COLLECTION}/${currentSlug}`
          );
        }
      }
    });
  } catch (error) {
    if (
      (error as { code?: unknown })?.code === "permission-denied" &&
      !diagnostics.existingBusinessOwnerId &&
      !diagnostics.existingBusinessStatus
    ) {
      setPredictedSlugReservationBlock(
        diagnostics,
        diagnostics.businessPath,
        "permission denied while reading existing business"
      );
    }

    logSafeApplicationError("Business slug reservation update failed.", error);
    throw error;
  }
}

export async function patchApprovedBusinessSafeFields(
  businessId: string,
  patch: Record<string, unknown>
) {
  const business = (await readBusiness(businessId)) as Record<
    string,
    unknown
  > | null;

  if (!business) {
    throw new Error("Business not found.");
  }

  if (normalizeBusinessStatus(business.status) !== "approved") {
    throw new Error("This helper is only for approved businesses.");
  }

  await patchBusiness(businessId, patch);
}

/* =====================================================
   STATUS ACTIONS
===================================================== */

export async function submitBusinessForReview(businessId: string) {
  if (!businessId) {
    throw new Error("submitBusinessForReview: missing businessId");
  }

  const user = requireCurrentUser();
  const diagnostics = {
    authUid: user.uid,
    businessId,
    firebaseCode: undefined as unknown,
    firebaseMessage: undefined as unknown,
  };

  try {
    const functions = getFunctions(app);
    const submit = httpsCallable<
      { businessId: string },
      { ok: boolean; businessId: string; status: "pending" }
    >(functions, "submitBusinessForReview");

    await submit({ businessId });
  } catch (error) {
    const missingRequirements =
      getSavedBusinessValidationRequirements(error);

    if (missingRequirements.length > 0) {
      throw new SavedBusinessValidationError(missingRequirements);
    }

    diagnostics.firebaseCode = (error as { code?: unknown })?.code;
    diagnostics.firebaseMessage = (error as { message?: unknown })?.message;

    logSafeApplicationError("Business review submission failed.", error);
    throw error;
  }
}

export async function moveBusinessBackToDraft(businessId: string) {
  await patchBusiness(businessId, {
    status: "draft" as BusinessStatus,
    isPublished: false,
    onboardingCompleted: false,
    onboardingStep: "basics",
  });
}

export async function withdrawPendingBusiness(
  businessId: string,
  ownerId: string
) {
  if (!businessId) {
    throw new Error("withdrawPendingBusiness: missing businessId");
  }

  const user = requireCurrentUser(ownerId);
  const businessRef = doc(db, BUSINESS_COLLECTION, businessId);
  const ownerRef = doc(db, "owners", user.uid);

  await runTransaction(db, async (transaction) => {
    const [businessSnap, ownerSnap] = await Promise.all([
      transaction.get(businessRef),
      transaction.get(ownerRef),
    ]);

    if (!businessSnap.exists()) {
      throw new Error("Business not found.");
    }

    if (!ownerSnap.exists()) {
      throw new Error("Owner profile not found.");
    }

    const data = businessSnap.data() as Record<string, unknown>;

    if (data.ownerId !== user.uid) {
      throw new Error("You can only withdraw your own business.");
    }

    if (data.isDeleted === true) {
      throw new Error("This business has been deleted.");
    }

    if (normalizeBusinessStatus(data.status) !== "pending") {
      throw new Error("Only pending businesses can be withdrawn.");
    }

    transaction.update(businessRef, {
      status: "draft" as BusinessStatus,
      isPublished: false,
      onboardingCompleted: false,
      onboardingStep: "basics",
      updatedAt: serverTimestamp(),
    });
    transaction.update(ownerRef, {
      onboardingStep: "basics",
    });
  });
}

export async function deleteDraftBusiness(
  businessId: string,
  ownerId: string
) {
  if (!businessId) {
    throw new Error("deleteDraftBusiness: missing businessId");
  }

  const user = requireCurrentUser(ownerId);
  const refDoc = doc(db, BUSINESS_COLLECTION, businessId);

  try {
    const snap = await getDoc(refDoc);

    if (!snap.exists()) {
      throw new Error("Business draft not found.");
    }

    const data = snap.data() as {
      ownerId?: string;
      status?: BusinessStatus;
      isDeleted?: boolean;
    };

    if (data.ownerId !== user.uid) {
      throw new Error("You can only delete your own draft business.");
    }

    if (data.isDeleted === true) {
      return;
    }

    if (data.status !== "draft") {
      throw new Error("Only draft businesses can be deleted by the owner.");
    }

    await updateDoc(refDoc, {
      isDeleted: true,
      ownerDeletedAt: serverTimestamp(),
      ownerDeletedByUid: user.uid,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.warn("Business draft deletion failed.");
    throw error;
  }
}

export async function deactivateApprovedBusiness(
  businessId: string,
  ownerId: string
) {
  if (!businessId) {
    throw new Error("deactivateApprovedBusiness: missing businessId");
  }

  const user = requireCurrentUser(ownerId);
  const refDoc = doc(db, BUSINESS_COLLECTION, businessId);
  const snap = await getDoc(refDoc);

  if (!snap.exists()) {
    throw new Error("Business not found.");
  }

  const data = snap.data() as Record<string, unknown>;

  if (data.ownerId !== user.uid) {
    throw new Error("You can only deactivate your own business.");
  }

  if (data.isDeleted === true) {
    throw new Error("This business has been deleted.");
  }

  if (data.isDeactivated === true) {
    return;
  }

  if (
    normalizeBusinessStatus(data.status ?? data.triageStatus) !== "approved" ||
    data.isPublished !== true
  ) {
    throw new Error("Only approved, published businesses can be deactivated.");
  }

  await updateDoc(refDoc, {
    isDeactivated: true,
    deactivatedAt: serverTimestamp(),
    deactivatedByUid: user.uid,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteApprovedBusiness(
  businessId: string,
  ownerId: string
) {
  if (!businessId) {
    throw new Error("deleteApprovedBusiness: missing businessId");
  }

  const user = requireCurrentUser(ownerId);
  const refDoc = doc(db, BUSINESS_COLLECTION, businessId);
  const snap = await getDoc(refDoc);

  if (!snap.exists()) {
    throw new Error("Business not found.");
  }

  const data = snap.data() as Record<string, unknown>;

  if (data.ownerId !== user.uid) {
    throw new Error("You can only delete your own business.");
  }

  if (data.isDeleted === true) {
    return;
  }

  if (
    normalizeBusinessStatus(data.status ?? data.triageStatus) !== "approved" ||
    data.isPublished !== true
  ) {
    throw new Error("Only approved, published businesses can be deleted.");
  }

  if (data.isDeactivated !== true) {
    throw new Error("Deactivate this business before deleting it.");
  }

  await updateDoc(refDoc, {
    isDeleted: true,
    ownerDeletedAt: serverTimestamp(),
    ownerDeletedByUid: user.uid,
    updatedAt: serverTimestamp(),
  });
}

export async function reactivateApprovedBusiness(
  businessId: string,
  ownerId: string
) {
  if (!businessId) {
    throw new Error("reactivateApprovedBusiness: missing businessId");
  }

  const user = requireCurrentUser(ownerId);
  const refDoc = doc(db, BUSINESS_COLLECTION, businessId);
  const snap = await getDoc(refDoc);

  if (!snap.exists()) {
    throw new Error("Business not found.");
  }

  const data = snap.data() as Record<string, unknown>;

  if (data.ownerId !== user.uid) {
    throw new Error("You can only reactivate your own business.");
  }

  if (data.isDeleted === true) {
    throw new Error("This business has been deleted.");
  }

  if (data.isDeactivated !== true) {
    return;
  }

  if (
    normalizeBusinessStatus(data.status ?? data.triageStatus) !== "approved" ||
    data.isPublished !== true
  ) {
    throw new Error("Only approved, published businesses can be reactivated.");
  }

  await updateDoc(refDoc, {
    isDeactivated: false,
    reactivatedAt: serverTimestamp(),
    reactivatedByUid: user.uid,
    updatedAt: serverTimestamp(),
  });
}

export async function removeBusiness(businessId: string, ownerId: string) {
  if (!businessId) {
    throw new Error("removeBusiness: missing businessId");
  }

  requireCurrentUser(ownerId);

  throw new Error(
    "Hard delete is admin-only. Use deleteDraftBusiness() for drafts or deactivateApprovedBusiness() for approved businesses."
  );
}
